/**
 * Tracking wrapper — point d'entrée unique pour émettre un event vers Better Stack.
 *
 * V0 : ingestion HTTP vers la source `frontend-events` (BS team Homeys, eu-fsn-3).
 * L'enveloppe canonique est remplie depuis `auth.store` (user_id, actor_type,
 * company_id, company_type, role, subscriptions, session_id).
 *
 * **Garanties de résilience** (priorité absolue : ne JAMAIS impacter l'app) :
 * - Wrap try/catch externe : aucune exception ne propage hors de `track()`
 * - Fire-and-forget : appel synchrone, pas d'`await` requis
 * - Timeout 3s sur le POST : si BS hang, on abandonne sans bloquer
 * - BS down / 500 / token invalide → silencieux, l'app tourne
 *
 * Usage type-safe (l'event name discriminate le payload) :
 *
 *   import { track } from "@core/tracking/track"
 *   track({ event: "Session Started", payload: {} })
 *
 * Doc projet : `top/docs/projects/tracking-product-plan-v1.md`.
 */

import { useAuthStore } from "@core/stores/auth.store"
import type { TrackedEvent } from "./events"
import type { ActorType, CompanyType, Role, TrackingEnvelope } from "./envelope"

const BS_URL = import.meta.env.VITE_TRACKING_BS_URL as string | undefined
const BS_TOKEN = import.meta.env.VITE_TRACKING_BS_TOKEN as string | undefined

/** Timeout d'ingestion BS (ms). Au-delà → abandon silencieux. */
const INGEST_TIMEOUT_MS = 3000

/** Fallback compagnie type si non encore chargé côté store. */
const DEFAULT_COMPANY_TYPE: CompanyType = "entreprise"
/** Fallback role si non encore chargé côté store. */
const DEFAULT_ROLE: Role = "employe"

interface StripeSubscriptionLike {
    metadata?: { tag?: string }
    status?: string
}

function extractSubscriptionTags(
    subscriptions: readonly StripeSubscriptionLike[] | undefined
): string[] {
    if (!subscriptions || subscriptions.length === 0) return []
    const tags: string[] = []
    for (const sub of subscriptions) {
        if (sub.status === "canceled") continue
        const raw = sub.metadata?.tag
        if (!raw) continue
        for (const t of raw.split(",")) {
            const trimmed = t.replace(/\s/g, "")
            if (trimmed) tags.push(trimmed)
        }
    }
    return tags
}

/**
 * Émet un event tracking. Le `event` discriminate le `payload` au type-check.
 *
 * Skip silencieusement si :
 * - VITE_TRACKING_BS_URL/TOKEN manquants (build sans tracking)
 * - utilisateur non authentifié (V0 ne track que les sessions auth)
 * - Pinia pas encore prête (très tôt au boot)
 *
 * Garantit : aucune exception, aucun blocage, aucun impact UX si BS down.
 *
 * @param event L'event typé (cf. ./events.ts)
 */
export function track<E extends TrackedEvent>(event: E): void {
    try {
        if (!BS_URL || !BS_TOKEN) {
            if (import.meta.env.DEV) {
                console.debug(
                    "[track] Skipped (BS not configured): VITE_TRACKING_BS_URL/TOKEN missing"
                )
            }
            return
        }

        let authStore: ReturnType<typeof useAuthStore>
        try {
            authStore = useAuthStore()
        } catch {
            // Pinia pas encore initialisée (très rare, juste au boot)
            return
        }

        if (!authStore.isLogged || !authStore.userId) {
            // V0 : on skip les events tant qu'on n'a pas l'utilisateur authentifié.
            return
        }

        const actorType: ActorType = authStore.adminHomeys
            ? "homeys_staff"
            : "client"

        const envelope: TrackingEnvelope = {
            timestamp: new Date().toISOString(),
            event: event.event,
            event_version: 1,

            user_id: authStore.userId,
            actor_type: actorType,
            session_id: authStore.sessionId,

            company_id: authStore.companyId,
            company_type: authStore.companyType ?? DEFAULT_COMPANY_TYPE,
            subscription_plan:
                (authStore.subscription as { plan?: string } | undefined)
                    ?.plan ?? "",
            role: authStore.role ?? DEFAULT_ROLE,
            subscriptions: extractSubscriptionTags(
                authStore.companyStripeSubscriptions as
                    | readonly StripeSubscriptionLike[]
                    | undefined
            ),

            payload: event.payload as Record<string, unknown>,
        }

        let body: string
        try {
            body = JSON.stringify(envelope)
        } catch {
            // Payload contient un cycle ou une valeur non-serializable.
            return
        }

        if (import.meta.env.DEV) {
            console.debug("[track]", envelope)
        }

        // Fire-and-forget POST avec timeout. Toute erreur (réseau, 5xx, abort) est silencieuse.
        const controller =
            typeof AbortController !== "undefined"
                ? new AbortController()
                : null
        const timeoutId = controller
            ? setTimeout(() => controller.abort(), INGEST_TIMEOUT_MS)
            : null

        fetch(BS_URL, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${BS_TOKEN}`,
            },
            body,
            signal: controller?.signal,
            // pas de keepalive : provoque des reports 503 mensongers en DevTools
            // alors que BS reçoit bien (vu en prod 2026-05-07). Trade-off accepté V0 :
            // les events émis pendant un unload de page peuvent être perdus.
        })
            .catch(() => {
                // Ingestion silencieuse : on ne propage rien.
            })
            .finally(() => {
                if (timeoutId !== null) clearTimeout(timeoutId)
            })
    } catch {
        // Garde-fou final : si quoi que ce soit ci-dessus throw (improbable),
        // on absorbe sans propager. Le tracking ne doit JAMAIS planter l'app.
    }
}
